import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUPPORTED_LOCALES, SupportedLocale, ensureLocaleLoaded } from '../i18n';

interface LocaleContextType {
  currentLocale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  getSupportedLocales: () => typeof SUPPORTED_LOCALES;
  getLocalizedPath: (path: string, locale?: SupportedLocale) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

interface LocaleProviderProps {
  children: ReactNode;
  currentLocale: SupportedLocale;
}

export const LocaleProvider = ({ children, currentLocale }: LocaleProviderProps) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [locale, setLocaleState] = useState<SupportedLocale>(currentLocale);

  // Update i18n language when locale changes (loading its split bundle first)
  useEffect(() => {
    if (i18n.language !== locale) {
      let cancelled = false;
      ensureLocaleLoaded(locale).then(() => {
        if (!cancelled) i18n.changeLanguage(locale);
      });
      return () => {
        cancelled = true;
      };
    }
    setLocaleState(locale);
  }, [locale, i18n]);

  // Update locale when currentLocale prop changes (from route)
  useEffect(() => {
    if (currentLocale !== locale) {
      setLocaleState(currentLocale);
    }
  }, [currentLocale, locale]);

  const setLocale = (newLocale: SupportedLocale) => {
    // Get current path without locale
    const pathWithoutLocale = location.pathname.replace(/^\/[a-z]{2}-[A-Z]{2}/, '') || '/';
    
    // Remember the user's preference
    localStorage.setItem('preferred-locale', newLocale);
    
    // Navigate to new locale path
    const newPath = `/${newLocale}${pathWithoutLocale}${location.search}${location.hash}`;
    navigate(newPath);
  };

  const getSupportedLocales = () => SUPPORTED_LOCALES;

  const getLocalizedPath = (path: string, targetLocale?: SupportedLocale) => {
    const useLocale = targetLocale || locale;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${useLocale}${cleanPath}`;
  };

  const value: LocaleContextType = {
    currentLocale: locale,
    setLocale,
    getSupportedLocales,
    getLocalizedPath
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};